import type {
  GenericError,
  UserDetails,
  PaymentDetails,
} from 'src/types/referrer-portal';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/index';


export const DEFAULT_API_ERROR: GenericError = {
  field_errors: {},
  non_field_errors: [
    {
      type: 'default',
      message: 'Some Error occurred',
    },
  ],
};

export const SUCCESS_PAYLOAD = {
  message: 'success',
} as const;

export const ERROR_CODE = {
  PaymentMethodAlreadyExists: 'Payment Method Already Exists',
  ServerError: 'Internal Server Error',
} as const;

export const getReferralNotification = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  title: string;
  description: string;
  key: string;
}> => [
  {
    title: t('Submitted', 'Submitted'),
    description: `${t(
      'Get_email_confirmation_every_time_you_submit_a_referral',
      'Get email confirmation every time you submit a referral',
    )}.`,
    key: 'submission',
  },
  {
    title: t('Withdrawn', 'Withdrawn'),
    description: `${t(
      'Get_email_notification_when_candidate_referred_by_you_withdraw_their_application',
      'Get email notification when candidate referred by you withdraw their application',
    )}.`,
    key: 'withdrawn',
  },
  {
    title: t('Status_Change', 'Status Change'),
    description: `${t(
      'Get_email_notification_when_the_status_of_a_referred_candidate_changes',
      'Get email notification when the status of a referred candidate changes',
    )}.`,
    key: 'status_change',
  },
  {
    title: t('Pending_Payout', 'Pending Payout'),
    description: `${t(
      'Get_notified_when_you_are_eligible_for_the_referral_bonus',
      'Get notified when you are eligible for the referral bonus',
    )}.`,
    key: 'payments',
  },
];

export const getUserFormInputFields = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'name', label: t('Name', 'Name')},
  {key: 'email', label: t('Email', 'Email')},
  {key: 'phone_number', label: t('Phone_Number', 'Phone Number')},
  {key: 'location', label: t('Location', 'Location')},
  {key: 'company_name', label: t('Company_Name', 'Company Name')},
  {key: 'position', label: t('TitlePosition', 'Title/Position')},
];

export const getBankInputFields = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    key: 'name',
    label: t('Name_as_per_Bank_Account', 'Name as per Bank Account'),
  },
  {key: 'bank_name', label: t('Bank_Name', 'Bank Name')},
  {key: 'account_number', label: t('Account_Number', 'Account Number')},
  {key: 'routing_number', label: t('Routing_Number', 'Routing Number')},
  {key: 'swift_code', label: t('Swift_Code', 'Swift Code')},
];

export const userFormInitialValue: UserDetails = {
  id: null,
  name: '',
  email: '',
  profile_picture_url: '',
  phone_number: '',
  position: '',
  bio: '',
  company_name: '',
  location: '',
};

export const paymentInitialData: PaymentDetails = {
  method: 'paypal',
  details: {
    paypal_id: '',
    name: '',
    bank_name: '',
    account_number: '',
    swift_code: '',
    routing_number: '',
  },
};

export const PROFILE_SETUP_STEEPER = {
  user: 0,
  payment: 1,
} as const;

export const getSettingsTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  tabId: string;
  label?: string;
}> => [
  {tabId: '/settings/profile', label: t('Profile', 'Profile')},
  {
    tabId: '/settings/preferences',
    label: t('Preferences', 'Preferences'),
  },
];

export const routeToProduct = {
  dashboard: '/dashboard',
  referrals: '/referrals',
  jobs: '/jobs',
  earnings: '/earnings',
  settings: '/settings',
} as const;

export const PRODUCT_LISTS = [
  {product: 'dashboard', iconName: 'chart-column'},
  {product: 'referrals', iconName: 'user-check'},
  {product: 'jobs', iconName: 'briefcase'},
  {product: 'earnings', iconName: 'wallet'},
  {product: 'settings', iconName: 'gear'},
] as const;

export const USER_FORM_URL_SEGMENT = '/u/';

export const getReferrerPortalAssignJobHeader = (
  t: (arg1: string, arg2: string) => string,
): {
  agencyStepLabel: string;
  agencyStepContent: string;
  agencyStepCardHeader: string;
} => ({
  agencyStepLabel: t(
    'Select_Organisation_Referral_Program',
    'Select Organisation & Referral Program',
  ),
  agencyStepContent: t(
    'Choose_a_company_you_would_like_to_be_referred',
    'Choose a company you would like to be referred',
  ),
  agencyStepCardHeader: t(
    'Organisation_Program_Details',
    'Organisation & Program Details',
  ),
});

export const getDefaultAssignJobHeader = (
  t: (arg1: string, arg2: string) => string,
): {
  agencyStepLabel: string;
  agencyStepContent: string;
  agencyStepCardHeader: string;
} => ({
  agencyStepLabel: t('Select_Referral_Program', 'Select Referral Program'),
  agencyStepContent: t(
    'Choose_the_program_you_would_like_to_be_referred_to',
    'Choose the program you would like to be referred to',
  ),
  agencyStepCardHeader: t('Program_Details', 'Program Details'),
});
